import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, MoreHorizontal, Plus, Clock, Briefcase, Layers, X } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { useWithBack } from "@/lib/nav";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { formatDateOnly } from "@/lib/format";

const WINDOWS = [
  { value: "14", label: "Next 14 days" },
  { value: "30", label: "Next 30 days" },
  { value: "60", label: "Next 60 days" },
  { value: "90", label: "Next 90 days" },
];

function dueTone(item) {
  if (item.overdue) return "danger";
  if (item.days_delta <= 7) return "warning";
  return "info";
}

function dueLabel(item) {
  if (item.overdue) return `${Math.abs(item.days_delta)}d overdue`;
  if (item.days_delta === 0) return "Due today";
  return `Due in ${item.days_delta}d`;
}

export default function DueSoonPage() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canBatch = hasPermission("job.write");
  const [items, setItems] = useState([]);
  const [withinDays, setWithinDays] = useState("30");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [batching, setBatching] = useState(false);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { within_days: Number(withinDays) };
      if (overdueOnly) params.overdue = true;
      const { data } = await api.get("/service/due-soon", { params });
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items || []);
      setSelected(new Set());
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [withinDays, overdueOnly]);

  useEffect(() => { load(); }, [load]);

  const snooze = async (row) => {
    try {
      await api.post(`/service/snooze/${row.machine_id}`, { days: 7 });
      toast.success(`Snoozed 7 days — ${row.machine_label}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createJob = (row) => {
    const params = new URLSearchParams({
      customer_id: row.customer_id || "",
      site_id: row.site_id || "",
      machine_id: row.machine_id || "",
    });
    navigate(`/jobs/new?${params.toString()}`);
  };

  const toggleRow = (mid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length && items.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((r) => r.machine_id).filter(Boolean)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const createBatch = async () => {
    if (!canBatch || selected.size === 0 || batching) return;
    const machine_ids = Array.from(selected);
    setBatching(true);
    try {
      const { data } = await api.post("/jobs/batch-from-due-soon", { machine_ids });
      const created = data.created?.length || 0;
      const failed = data.failed?.length || 0;
      if (created > 0 && failed === 0) {
        toast.success(`Created ${created} job${created === 1 ? "" : "s"}`);
      } else if (created > 0 && failed > 0) {
        toast.warning(`Created ${created} · ${failed} failed`);
      } else {
        toast.error(`No jobs created (${failed} failed)`);
      }
      if (failed > 0) {
        for (const f of data.failed) {
          toast.error(`${f.machine_id.slice(0, 8)}…: ${f.error}`);
        }
      }
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBatching(false);
    }
  };

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="due-soon-page">
      <PageHeader
        testId="due-soon-header"
        title="Due Soon"
        subtitle="Machines with a scheduled service coming up — or already overdue."
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={withinDays} onValueChange={setWithinDays}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="due-soon-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Overdue only</Label>
            <Switch checked={overdueOnly} onCheckedChange={setOverdueOnly} data-testid="due-soon-overdue-only" />
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {loading ? "Loading…" : `${items.length} machine${items.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="due-soon-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {canBatch && (
                  <th className="w-8 py-2 pl-3">
                    <Checkbox
                      checked={items.length > 0 && selected.size === items.length}
                      onCheckedChange={toggleAll}
                      aria-label="Select all rows"
                      data-testid="due-soon-select-all"
                    />
                  </th>
                )}
                <th className="text-left py-2 px-3">Machine</th>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Site</th>
                <th className="text-left py-2 px-3">Next due</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Open job?</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canBatch ? 8 : 7} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={canBatch ? 8 : 7} className="py-8 text-center text-xs text-muted-foreground" data-testid="due-soon-empty">
                  Nothing due in this window. Nice.
                </td></tr>
              ) : items.map((row) => (
                <tr key={row.machine_id} data-testid={`due-soon-row-${row.machine_id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  {canBatch && (
                    <td className="py-2 pl-3 align-middle">
                      <Checkbox
                        checked={selected.has(row.machine_id)}
                        onCheckedChange={() => toggleRow(row.machine_id)}
                        aria-label={`Select ${row.machine_label}`}
                        data-testid={`due-soon-select-${row.machine_id}`}
                      />
                    </td>
                  )}
                  <td className="py-2 px-3">
                    <Link to={withBackHref(`/machines/${row.machine_id}`)} className="font-medium hover:text-primary" data-testid={`due-soon-machine-link-${row.machine_id}`}>
                      {row.machine_label || "—"}
                    </Link>
                    <div className="text-xs text-muted-foreground font-mono">Serial {row.machine_serial || "—"}</div>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    {row.customer_id ? (
                      <Link to={withBackHref(`/customers/${row.customer_id}`)} className="hover:text-primary" data-testid={`due-soon-customer-link-${row.customer_id}`}>{row.customer_name || "—"}</Link>
                    ) : "—"}
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    {row.site_id ? (
                      <Link to={withBackHref(`/sites/${row.site_id}`)} className="hover:text-primary" data-testid={`due-soon-site-link-${row.site_id}`}>{row.site_name || "—"}</Link>
                    ) : "—"}
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap">{formatDateOnly(row.next_due_at)}</td>
                  <td className="py-2 px-3">
                    <StatusBadge tone={dueTone(row)} testId={`due-soon-status-${row.machine_id}`}>{dueLabel(row)}</StatusBadge>
                  </td>
                  <td className="py-2 px-3">
                    {row.has_open_job
                      ? <StatusBadge tone="info">Open job</StatusBadge>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`due-soon-actions-${row.machine_id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => createJob(row)} data-testid={`due-soon-create-job-${row.machine_id}`}>
                          <Plus className="h-4 w-4 mr-2" /> Create Job
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/machines/${row.machine_id}`)} data-testid={`due-soon-view-machine-${row.machine_id}`}>
                          <Briefcase className="h-4 w-4 mr-2" /> View machine
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => snooze(row)} data-testid={`due-soon-snooze-${row.machine_id}`}>
                          <Clock className="h-4 w-4 mr-2" /> Snooze 7d
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        <CalendarCheck className="h-3 w-3 inline mr-1 align-[-2px]" />
        Next due dates recompute automatically when a service job is closed.
      </p>

      {canBatch && selected.size > 0 && (
        <div
          className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex items-center justify-between z-30"
          data-testid="due-soon-batch-bar"
        >
          <div className="flex items-center gap-3">
            <Layers className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium" data-testid="due-soon-batch-count">
              {selected.size} machine{selected.size === 1 ? "" : "s"} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="due-soon-batch-clear"
            >
              <X className="h-3 w-3" /> Clear selection
            </button>
          </div>
          <Button
            onClick={createBatch}
            disabled={batching || selected.size === 0}
            data-testid="due-soon-batch-create"
          >
            <Plus className="h-4 w-4" />
            <span className="ml-1">
              {batching ? "Creating…" : `Create ${selected.size} job${selected.size === 1 ? "" : "s"}`}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}
